/**
 * useIsMobile — true when the viewport is at or below the mobile breakpoint
 * (default 760px, the X.6.2 chrome breakpoint).
 *
 * AppShell uses this to swap the desktop center pill nav for the fixed bottom
 * MobileTabBar (and to drop the desktop-only search button + shrink the bar).
 * Driving the swap off ONE JS hook (rather than CSS media queries) keeps a
 * single breakpoint source of truth shared by every chrome surface.
 *
 * matchMedia-based with a live `change` listener. SSR-safe: returns `false`
 * (desktop) when window/matchMedia is unavailable — a server render, or jsdom
 * without a matchMedia shim — so a non-browser environment never assumes
 * mobile and the desktop chrome is the safe default.
 */

import { useEffect, useState } from 'react';

export const MOBILE_BREAKPOINT_PX = 760;

function mediaQuery(breakpointPx: number): string {
  return `(max-width: ${breakpointPx}px)`;
}

function matches(breakpointPx: number): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(mediaQuery(breakpointPx)).matches;
}

export function useIsMobile(breakpointPx: number = MOBILE_BREAKPOINT_PX): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => matches(breakpointPx));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(mediaQuery(breakpointPx));
    const onChange = (): void => setIsMobile(mql.matches);
    // Re-sync once on mount in case the viewport changed between the initial
    // useState read and the effect firing.
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [breakpointPx]);

  return isMobile;
}
